/**
 * Conversation sidebar for Assistant UI
 * Matches the existing AssistantCore Sidebar component
 */
import React, { memo } from "react";
import { SquarePen, PanelLeftClose, PanelLeftOpen, CircleHelp } from "lucide-react";
import { ConversationItem } from "~/components/assistant/ConversationItem";
import { IconButton } from "~/components/assistant/PopoverButton";
import { SharedConversationItem } from "~/components/assistant/SharedConversationItem";
import { ZENDESK_HELP_URL } from "~/components/assistant/constants";
import type { Conversation, ConversationShortcut } from "~/components/assistant/types";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";

interface ConversationSidebarProps {
  conversations: Conversation[];
  currentConversationId: string | null;
  conversationShortcuts?: ConversationShortcut[];
  onNewChat: () => void;
  onSelect: (conversationId: string) => void;
  onDelete: (conversationId: string, event: React.MouseEvent) => void;
  onRename: (conversationId: string, displayName: string) => void;
  onToggle?: () => void;
  isCollapsed?: boolean;
}

interface SidebarActionProps {
  icon: React.ReactNode;
  label: string;
  isCollapsed: boolean;
  onClick?: () => void;
  href?: string;
}

function SidebarAction({ icon, label, isCollapsed, onClick, href }: SidebarActionProps) {
  const className = cn(
    "grid h-9 items-center rounded text-[13px] cursor-pointer hover:bg-gray-100",
    isCollapsed ? "w-9 grid-cols-[36px]" : "w-full grid-cols-[36px_1fr] text-left"
  );

  const content = (
    <>
      <span className="grid h-9 w-9 place-items-center">{icon}</span>
      {!isCollapsed && <span className="truncate">{label}</span>}
    </>
  );

  const actionElement = href ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      aria-label={label}
    >
      {content}
    </a>
  ) : (
    <button onClick={onClick} className={className} aria-label={label}>
      {content}
    </button>
  );

  if (!isCollapsed) {
    return actionElement;
  }

  return (
    <Tooltip delayDuration={700}>
      <TooltipTrigger asChild>{actionElement}</TooltipTrigger>
      <TooltipContent>
        <p>{label}</p>
      </TooltipContent>
    </Tooltip>
  );
}

export const ConversationSidebar = memo(function ConversationSidebar({
  conversations,
  currentConversationId,
  conversationShortcuts,
  onNewChat,
  onSelect,
  onDelete,
  onRename,
  onToggle,
  isCollapsed = false,
}: ConversationSidebarProps) {
  const visibleShortcuts = conversationShortcuts?.filter((s) => s.url) ?? [];
  const formatConversationLabel = (conversation: Conversation) => {
    if (conversation.display_name) {
      return conversation.display_name;
    }

    const date = new Date(conversation.created_at);
    const now = new Date();

    const timeString = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    const isToday = date.toDateString() === now.toDateString();
    const isYesterday = (() => {
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      return date.toDateString() === yesterday.toDateString();
    })();

    let dateString: string;

    if (isToday) {
      dateString = "Today";
    } else if (isYesterday) {
      dateString = "Yesterday";
    } else {
      dateString = date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }

    return `${dateString}, ${timeString}`;
  };

  return (
    <div
      className={cn(
        "flex flex-col h-full p-2 space-y-0.5",
        isCollapsed ? "w-auto" : "w-64 min-w-64",
        isCollapsed ? "" : "border-r border-gray-200"
      )}
    >
      {onToggle && (
        <Tooltip delayDuration={700}>
          <TooltipTrigger asChild>
            <IconButton
              onClick={onToggle}
              variant="ghost"
              className="self-start h-9 w-9 p-0 justify-center"
            >
              {isCollapsed ? (
                <PanelLeftOpen className="size-4.5" />
              ) : (
                <PanelLeftClose className="size-4.5" />
              )}
            </IconButton>
          </TooltipTrigger>
          <TooltipContent>
            <p>{isCollapsed ? "Show recents" : "Hide recents"}</p>
          </TooltipContent>
        </Tooltip>
      )}

      <div className="space-y-0.5">
        <SidebarAction
          icon={<SquarePen className="size-4.5" />}
          label="New Analysis"
          isCollapsed={isCollapsed}
          onClick={onNewChat}
        />
        <SidebarAction
          icon={<CircleHelp className="size-4.5" />}
          label="Usage Guide"
          isCollapsed={isCollapsed}
          href={ZENDESK_HELP_URL}
        />
      </div>

      {!isCollapsed && (
        <>
          <div className="flex-1 overflow-y-auto scrollbar-subtle">
            {visibleShortcuts.length > 0 && (
              <>
                <div className="px-2 py-1 mb-2">
                  <h3 className="text-xs font-medium text-gray-500 tracking-wide">Shared</h3>
                </div>
                <div className="space-y-0.5 mb-2">
                  {visibleShortcuts.map((shortcut, index) => (
                    <SharedConversationItem key={index} label={shortcut.label} url={shortcut.url} />
                  ))}
                </div>
              </>
            )}
            {conversations.length > 0 && (
              <div className="px-2 py-1 mb-2">
                <h3 className="text-xs font-medium text-gray-500 tracking-wide">Recents</h3>
              </div>
            )}
            <div className="space-y-0.5">
              {conversations.map((conversation) => (
                <ConversationItem
                  key={conversation.id}
                  conversation={conversation}
                  isSelected={conversation.id === currentConversationId}
                  isStreaming={false}
                  onSelect={onSelect}
                  onDelete={onDelete}
                  onRename={onRename}
                  formatLabel={formatConversationLabel}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
});
